/**
 * The Settings "Pool" card's rename control.
 *
 * `POST /pool/update` has accepted a `name` since it shipped and writes
 * `nameLower` in lockstep so a renamed pool is searchable immediately, but no
 * screen ever sent one: Settings exposed the banner, joining, rules, season
 * and awards and never the name, which made a typo at creation permanent.
 * This is the missing control and nothing more.
 *
 * Explicit save, unlike the Joining card's save-on-toggle rows. A checkbox has
 * one moment where the fan is done deciding; a text field does not, and
 * writing on every keystroke or on blur turns a half typed name into a real
 * rename that everyone in the pool sees. `SavedNote` carries the outcome, as
 * it does on the toggles, so a rename never announces itself with a toast.
 */
import { usePoolUpdate } from "@sportsmet/pool-sdk/private";
import { Button, Input, SavedNote } from "@sportsmet/ui";
import { type ReactNode, useEffect, useState } from "react";

import { showApiError, showOfflineError } from "../../errors/apiErrorBus";
import {
  POOL_NAME_HINT,
  POOL_NAME_LABEL,
  POOL_NAME_MAX_LENGTH,
  POOL_NAME_SAVE_LABEL,
  resolvePoolNameFieldState,
} from "./poolNameFieldState";

export type typePoolSettingsNameFieldProps = {
  poolId: string;
  /** The saved name from `POST /pool/get`. Null while that read is in flight. */
  poolName: string | null;
};

export const PoolSettingsNameField = ({
  poolId,
  poolName,
}: typePoolSettingsNameFieldProps): ReactNode => {
  const update = usePoolUpdate();
  const [draft, setDraft] = useState<string | null>(null);
  const [isSaved, setIsSaved] = useState(false);

  // The field follows the query until the fan touches it: `draft` stays null
  // and the input shows whatever `/pool/get` last returned. Once they type,
  // their text wins, because the generated hooks refetch `/pool/get` after
  // every OTHER settings write on this screen and a refetch must never
  // overwrite a name somebody is halfway through typing.
  //
  // Switching pools is the one thing that discards a draft. The same pane
  // instance serves whichever pool `?poolId=` names, so without this a name
  // typed for one pool would be sitting in the box for the next one.
  useEffect(() => {
    setDraft(null);
    setIsSaved(false);
  }, [poolId]);

  const value = draft ?? poolName ?? "";
  const state = resolvePoolNameFieldState({
    draft: value,
    savedName: poolName,
  });

  async function handleSave(): Promise<void> {
    if (state.nextName === null) return;
    try {
      const result = await update.mutateAsync({
        poolId,
        name: state.nextName,
      });
      if (showApiError(result)) return;
      // Back to following the query. The refetch this write triggers returns
      // the saved name, so dropping the draft leaves the field showing what
      // the server actually stored rather than what was typed.
      setDraft(null);
      setIsSaved(true);
    } catch {
      showOfflineError();
    }
  }

  return (
    <div className="flex flex-col gap-2" data-testid="pool-settings-name-field">
      <Input
        label={POOL_NAME_LABEL}
        hint={POOL_NAME_HINT}
        value={value}
        maxLength={POOL_NAME_MAX_LENGTH}
        data-testid="pool-settings-name-input"
        {...(state.error !== null ? { error: state.error } : {})}
        onChange={(event) => {
          setDraft(event.target.value);
          setIsSaved(false);
        }}
      />
      <div className="flex items-center gap-3">
        <Button
          variant="secondary"
          size="sm"
          data-testid="pool-settings-name-save"
          disabled={!state.isSaveEnabled || update.isPending}
          onClick={() => {
            void handleSave();
          }}
        >
          {POOL_NAME_SAVE_LABEL}
        </Button>
        <div data-testid="pool-settings-name-saved">
          <SavedNote saved={isSaved} />
        </div>
      </div>
    </div>
  );
};
